import React, { useState, useEffect } from "react";
import { useThread } from "../context/ThreadContext";
import { ThreadService } from "../services/ThreadService";
import { db } from "../firebase.config";
import { collection, query, where, getDocs, or } from "firebase/firestore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Mail,
  FileText,
  Clock,
  FileCode,
  Briefcase,
  Megaphone,
  Search,
  Info,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const CoPilotsDialog = ({ isOpen, onClose, currentUser }) => {
  const { dispatch } = useThread();
  const [orgPrompts, setOrgPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  const defaultCopilots = [
    {
      name: "E-mail Writer",
      description:
        "Craft professional and effective emails with intelligent assistance",
      systemPrompt:
        "You are an email writing assistant. Your role is to help users craft professional, clear, and effective emails. First, ask about the purpose, recipient, and key points they want to convey.",
      userPrompt:
        "Hello! I can help you write professional emails. Could you tell me about the email you want to write? Who is it for and what is the main purpose?",
      category: "writing",
    },
    {
      name: "Report Maker",
      description:
        "Create comprehensive and well-structured reports effortlessly",
      systemPrompt:
        "You are a report writing assistant. Your role is to help users create well-structured, comprehensive reports. First, ask about the topic, target audience, and key findings or information that needs to be included.",
      userPrompt:
        "Hi! I can help you create a detailed report. What topic would you like to write about, and who is your target audience?",
      category: "business",
    },
    {
      name: "Itinerary Maker",
      description:
        "Plan detailed travel schedules tailored to your preferences",
      systemPrompt:
        "You are a travel itinerary assistant. Your role is to help users plan detailed and organized travel schedules. First, ask about their destination, duration of stay, preferences for activities, and budget considerations.",
      userPrompt:
        "Welcome! I can help you plan your travel itinerary. Where would you like to go, and how long are you planning to stay?",
      category: "planning",
    },
  ];

  useEffect(() => {
    if (isOpen && currentUser?.org_id) {
      loadOrgPrompts(currentUser.org_id);
    } else if (isOpen) {
      setOrgPrompts([]);
      setLoading(false);
    }
  }, [isOpen, currentUser]);

  const loadOrgPrompts = async (orgId) => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "prompts"),
        where("org_id", "==", orgId),
        where("isActive", "==", true),
        where(or(where("type", "==", "prompt"), where("type", "==", null)))
      );

      const querySnapshot = await getDocs(q);
      const prompts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrgPrompts(prompts);
    } catch (error) {
      console.error("Error loading organization prompts:", error);
      setOrgPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "writing":
        return <Mail className="w-8 h-8" />;
      case "business":
        return <Briefcase className="w-8 h-8" />;
      case "coding":
        return <FileCode className="w-8 h-8" />;
      case "marketing":
        return <Megaphone className="w-8 h-8" />;
      case "research":
        return <Search className="w-8 h-8" />;
      case "planning":
        return <Clock className="w-8 h-8" />;
      case "document":
        return <FileText className="w-8 h-8" />;
      default:
        return <Info className="w-8 h-8" />;
    }
  };

  const handleCoPilotSelect = async (copilot) => {
    try {
      const threadId = await ThreadService.createThreadWithSystem(
        currentUser.uid,
        copilot.name,
        copilot.systemPrompt,
        copilot.userPrompt
      );
      const newThread = await ThreadService.getUserThreads(currentUser.uid);
      dispatch({ type: "SET_THREADS", payload: newThread });
      dispatch({ type: "SET_CURRENT_THREAD", payload: newThread[0] });
      onClose();
    } catch (error) {
      console.error("Error creating copilot thread:", error);
    }
  };

  const copilots = orgPrompts.length > 0 ? orgPrompts : defaultCopilots;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-bold tracking-tight">
            Choose Your Co-pilot
          </DialogTitle>
        </DialogHeader>
        <div className="p-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col space-y-3">
                  <Skeleton className="h-[125px] rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[150px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {copilots.map((copilot, index) => (
                <button
                  key={index}
                  onClick={() => handleCoPilotSelect(copilot)}
                  className="group relative flex flex-col items-center text-left p-6 rounded-lg border border-border bg-card transition-all duration-200 hover:border-primary hover:shadow-lg"
                >
                  <div className="mb-4 p-2 rounded-full bg-primary/10 text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                    {getCategoryIcon(copilot.category)}
                  </div>
                  <h3 className="text-lg font-semibold mb-2 w-full text-center">{copilot.name}</h3>
                  {copilot.description && (
                    <p className="text-sm text-muted-foreground w-full text-center">
                      {copilot.description}
                    </p>
                  )}
                  <div className="absolute inset-0 rounded-lg ring-2 ring-transparent transition-all duration-200 group-hover:ring-primary/20" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoPilotsDialog;
